import { MessageCircle, Download, ShieldCheck, Award } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { waLink } from "@/lib/site";
import { track } from "@/lib/analytics";

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#0e3b21] text-white pt-28 sm:pt-32 pb-20 sm:pb-28 grain hex-grid"
    >
      {/* Decorative hexagons */}
      <div
        aria-hidden
        className="absolute -top-16 -right-20 w-[380px] h-[380px] hex bg-[#c9a24c]/10 blur-[2px] hidden sm:block"
      />
      <div
        aria-hidden
        className="absolute -bottom-24 -left-16 w-[300px] h-[300px] hex bg-[#c9a24c]/5 hidden sm:block"
      />
      <div
        aria-hidden
        className="absolute top-1/2 right-1/3 w-[120px] h-[120px] hex border-2 border-[#c9a24c]/25 hidden lg:block"
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="reveal reveal-1 inline-flex items-center gap-2 rounded-full border border-[#c9a24c]/40 bg-[#c9a24c]/10 px-4 py-1.5 text-[#e8cf90] text-xs sm:text-sm font-medium tracking-wide">
            <ShieldCheck size={14} /> Uso profissional · Qualidade garantida
          </div>

          <h1
            data-testid="hero-title"
            className="reveal reveal-2 mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Ciência química a serviço da{" "}
            <span className="text-[#c9a24c] italic">sua produção</span>.
          </h1>

          <p className="reveal reveal-3 mt-6 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed">
            Soluções especializadas em limpeza, proteção e manutenção para
            <strong className="text-white"> frigoríficos, indústria metalmecânica e agronegócio</strong>.
            Menos parada. Mais produtividade. Resultado onde importa.
          </p>

          <div className="reveal reveal-4 mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              data-testid="hero-whatsapp-btn"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { source: "hero" })}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold px-7 py-4 text-base shadow-xl transition-all hover:scale-[1.02]"
            >
              <MessageCircle size={18} strokeWidth={2.4} />
              Falar com um especialista
            </a>
            <a
              data-testid="hero-catalog-btn"
              href="#segmentos"
              onClick={() => track("catalog_cta_click", { source: "hero" })}
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#c9a24c] text-[#c9a24c] hover:bg-[#c9a24c] hover:text-[#0e3b21] font-semibold px-7 py-4 text-base transition-all"
            >
              <Download size={18} strokeWidth={2.4} />
              Ver catálogos
            </a>
          </div>

          <div className="reveal reveal-4 mt-10 grid grid-cols-3 gap-4 sm:gap-8 max-w-md">
            {[
              { n: "3", l: "Segmentos atendidos" },
              { n: "20L+", l: "Padrão profissional" },
              { n: "100%", l: "Fórmula concentrada" },
            ].map((s, i) => (
              <div key={i} className="text-left">
                <div
                  className="text-2xl sm:text-3xl font-bold text-[#c9a24c]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  {s.n}
                </div>
                <div className="text-[11px] sm:text-xs uppercase tracking-wider text-white/60 mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5 relative reveal reveal-3">
          <div className="relative aspect-[13/15] max-w-md mx-auto">
            {/* Big hex logo backdrop */}
            <div className="absolute inset-0 hex bg-gradient-to-br from-[#c9a24c]/25 via-[#0a2a17] to-[#0e3b21] shadow-2xl" />
            <div className="absolute inset-[6px] hex bg-[#0a2a17] flex items-center justify-center">
              <div className="text-center px-6">
                <LogoMark size={140} />
                <div className="mt-6 text-white font-extrabold text-2xl tracking-[0.15em]">
                  SEREQUIMICA
                </div>
                <div
                  className="mt-2 text-[#c9a24c] text-xs italic"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Indústria e Comércio de Produtos Químicos
                </div>
                <div className="mt-6 h-px w-16 bg-[#c9a24c] mx-auto" />
                <div className="mt-3 text-white/70 text-[11px] tracking-[0.25em]">
                  CIÊNCIA · TECNOLOGIA · RESULTADOS
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-5 -left-4 sm:-left-8 bg-white text-[#0e3b21] rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#0e3b21] text-[#c9a24c] flex items-center justify-center">
                <Award size={20} />
              </div>
              <div className="pr-2">
                <div className="text-xs text-[#0e3b21]/60 uppercase tracking-wider">
                  Padrão
                </div>
                <div className="font-bold text-sm">Uso Profissional</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom pillar strip (mirrors catalog footer) */}
      <div className="relative mt-16 sm:mt-20 border-t border-white/10 pt-6">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {["Alta Performance", "Segurança Operacional", "Eficiência Comprovada", "Qualidade Garantida"].map(
            (t, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-9 w-9 hex bg-[#c9a24c]/15 border border-[#c9a24c]/40 flex items-center justify-center">
                  <div className="h-2 w-2 rounded-full bg-[#c9a24c]" />
                </div>
                <div className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-white/85">
                  {t}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </section>
  );
}
